import Hls, {
  BufferAppendedData,
  BufferAppendingData,
  BufferCodecsData,
  ErrorData,
  Level,
  LevelSwitchedData,
  ManifestLoadingData,
  ManifestParsedData,
  MediaEndedData,
  SubtitleTracksUpdatedData,
  SubtitleTrackSwitchData
} from '@mediahub-bg/hls.js';
import hlsWorkerUrl from '@mediahub-bg/hls.js/dist/hls.worker.js';
import { removeUserDetails, resetVideo, setError } from 'common/actions';
import {
  ERROR_CODES_THAT_REQUIRE_LOGIN,
  PLAYER_ERROR_REDIRECT_MAP,
  REDIRECT_DEFAULT_ROUTE,
  STATUS_CODE_BAD_REQUEST
} from 'common/config/constants';
import variables from 'common/config/variables';
import { LiveChannels } from 'common/constants/data-types';
import {
  ChannelSetting,
  LANG_DEFAULT,
  ProfileSettings,
  RESOLUTION_AUTO,
  SettingsKey,
  SUBTITLES_OFF
} from 'common/reducers/profileSettings';
import { findClosestNumber } from 'common/services/helpers';
import { clearPrefetch, getPrefetchedBitrate, PrefetchLoader } from 'common/utils/hlsPrefetch';
import firebase from 'common/utils/firebase';
import GlobalStyle from 'common/utils/GlobalStyle';
import { findDeviceSettingsItem } from 'common/utils/helpers';
import { useRequiredData, useSignOut } from 'common/utils/hooks';
import { findIndex, findLastIndex, isEmpty, isNil, isNumber } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

export const parseError = (data: ErrorData): { status: number; subcode: number } | null => {
  try {
    let errorCode;
    if (data.networkDetails) {
      const { status, response } = data.networkDetails as XMLHttpRequest;
      if (typeof response === 'string' && response !== '') {
        errorCode = (JSON.parse(response) || {}).subcode as number;
        return { status: status as number, subcode: errorCode };
      }
    }
    return null;
  } catch (error) {
    console.info(error);
    return null;
  }
};

GlobalStyle.inject`
  .styled-video {
    width: 100%;
    height: 100%;
    outline: none;
  }
`;

export type THls = Hls & {
  defaultDvbAudioLanguage?: string;
};

interface VideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  channelId?: string;
  defaultDvbAudioLanguage?: string;
  dvbAudioTrack?: number;
  subtitleTrack?: number;
  startPosition?: number;
  startLevel?: number;
  autoPlay?: boolean;
  onSubtitlesUpdated?: (data: SubtitleTracksUpdatedData) => void;
  onSubtitleTrackSwitch?: (data: SubtitleTrackSwitchData) => void;
  onDvbAudioTracksUpdated?: (data: { audioTracks: DVBAudioTrack[] }) => void;
  onDvbAudioTrackSwitch?: (data: { id: number }) => void;
  onLevelSwitched?: (data: LevelSwitchedData, autoLevel?: boolean) => void;
  onLevelsUpdated?: (data: Level[], autoLevel?: boolean) => void;
  onHlsError?: (data: ErrorData) => void;
  onBufferAppended?: (data: BufferAppendedData) => void;
  onBufferAppending?: (data: BufferAppendingData) => void;
  onCodecCheck?: (supported: boolean, codec?: string) => void;
  setError: any;
  resetVideo: VoidFunction;
  removeUserDetails: any;
  hash?: string;
  // destroy hls.js without a source instead of keeping it for the next one
  destroyWithoutSource?: boolean;
  // Redux injected
  liveChannels: LiveChannels;
  profileSettings: ProfileSettings;
  //
}

export type VideoRefObject = {
  video?: HTMLVideoElement | null;
  hls?: THls | null;
  preload?: (
    src: string,
    channelId: string,
    onSeeking: () => void,
    onCancel: () => void
  ) => boolean;
};

export type DVBAudioTrack = {
  id: number;
  name: string;
  default: boolean;
};

// longest a preloaded zap holds back the store update
const PRELOAD_COMMIT_TIMEOUT = 1000;

const getScreenScale = () => {
  const scale = window.screen?.width / window.innerWidth;
  return scale > 0 && scale < 1 ? scale : Number.POSITIVE_INFINITY;
};

const Video = React.forwardRef<VideoRefObject, VideoProps>(function Video(
  props: VideoProps,
  customRef
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hls = useRef<THls | null>(null);
  const dvbAudioTracks = useRef<DVBAudioTrack[]>([]);
  const playWatchdogTimerId = useRef<NodeJS.Timeout | null>(null);
  const startPositionRef = useRef(-1);
  const sourceReadyNotified = useRef(false);
  // ready event held until the preloaded zap's store update renders
  const sourceReadyEventRef = useRef<React.SyntheticEvent<HTMLVideoElement, Event> | undefined>(
    undefined
  );
  const sourceBuffered = useRef(false);
  // what hls.js is loading, which runs ahead of props while a zap preloads
  const sourceRef = useRef<string | undefined>(undefined);
  const sourceChannelIdRef = useRef<string | undefined>(undefined);
  const preloadedSrcRef = useRef<string | undefined>(undefined);
  const finishPreloadRef = useRef<(() => void) | undefined>(undefined);
  const cancelPreloadRef = useRef<(() => void) | undefined>(undefined);
  const hashRef = useRef(props.hash);
  const freshMediaSourceRef = useRef(false);

  const propsRef = useRef(props);
  propsRef.current = props;

  const signOut = useSignOut();
  const { refreshToken } = useRequiredData();

  React.useImperativeHandle(
    customRef,
    () => ({
      video: videoRef.current,
      hls: hls.current,
      preload
    }),
    [videoRef.current, hls.current]
  );

  useEffect(() => {
    return destroyVideo;
  }, []);

  useEffect(() => {
    const preloaded = preloadedSrcRef.current;
    preloadedSrcRef.current = undefined;
    const hashChanged = !!props.hash && hashRef.current !== props.hash;
    hashRef.current = props.hash;
    if (props.src && props.src === preloaded && !hashChanged) {
      return;
    }
    if (props.src) {
      // a new hash asks for a fresh MediaSource, e.g. to recover an empty buffer
      freshMediaSourceRef.current = hashChanged;
      initVideo();
    } else if (props.destroyWithoutSource) {
      destroyVideo();
    } else {
      clearVideo();
    }
  }, [props.src, props.hash]);

  // Handle update on tracks based on props
  useEffect(() => {
    if (!hls.current) {
      return;
    }
    const { subtitleTrack, dvbAudioTrack } = props;
    if (dvbAudioTrack) {
      // hls.current.dvbAudioTrack = dvbAudioTrack;
    }
    if (subtitleTrack) {
      hls.current.subtitleTrack = subtitleTrack;
    }
  }, [props.dvbAudioTrack, props.subtitleTrack]);

  useEffect(() => {
    if (!isEmpty(hls.current?.subtitleTracks) && hls.current?.subtitleTracks) {
      switchToDefaultResource(hls.current?.subtitleTracks, 'subtitles', 'subtitleTrack');
    }
  }, [props.profileSettings?.react_tv_settings?.subtitles]);

  useEffect(() => {
    if (!isEmpty(dvbAudioTracks.current)) {
      switchToDefaultResource(dvbAudioTracks.current, 'audio', 'dvbAudioTrack');
    }
  }, [props.profileSettings?.react_tv_settings?.audio]);

  useEffect(() => {
    if (!isEmpty(props.profileSettings?.react_tv_settings?.resolution)) {
      handleInitialVideoLevel(hls.current?.levels || []);
    }
  }, [props.profileSettings?.react_tv_settings?.resolution]);

  const destroyVideo = () => {
    cancelPreloadRef.current?.();
    sourceReadyEventRef.current = undefined;
    if (hls.current) {
      hls.current?.destroy();
      hls.current = null;
    }
    if (videoRef.current) {
      videoRef.current.src = '';
      videoRef.current.load();
    }
  };

  // releases the stream but keeps hls.js and the video element for the next source
  const clearVideo = () => {
    cancelPreloadRef.current?.();
    sourceReadyEventRef.current = undefined;
    clearPlayWatchdogTimer();
    if (hls.current) {
      hls.current.stopLoad();
      hls.current.detachMedia();
    } else if (videoRef.current) {
      videoRef.current.src = '';
      videoRef.current.load();
    }
  };

  const getHlsOptions = () => {
    const defaultResource = resolvePreferredResource('audio');
    const defaultDvbAudioLanguage =
      propsRef.current.defaultDvbAudioLanguage || isNumber(defaultResource)
        ? 'und'
        : defaultResource;
    return {
      enableWorker: true,
      workerPath: hlsWorkerUrl,
      loader: PrefetchLoader,
      autoStartLoad: false,
      highBufferWatchdogPeriod: 1,
      nudgeOffset: 0.5,
      nudgeMaxRetry: 10,
      startPosition: startPositionRef.current,
      capLevelOnFPSDrop: true,
      capLevelToPlayerSize: true,
      startLevel: isNil(propsRef.current.startLevel) ? 0 : propsRef.current.startLevel,
      defaultDvbAudioLanguage,
      startFragPrefetch: true,
      reuseMediaSourceOnLoad: true,
      // chunks start on keyframes, which TV decoders seek to fastest
      startOnSegmentBoundary: true,
      liveSyncDurationCount: 3,
      // Reduce buffer size from default 60mb to 30mb for better handling on older TV's
      maxBufferSize: 30 * 1000 * 1000,
      // low-memory TVs can't keep all played video
      backBufferLength: 10,
      frontBufferFlushThreshold: 60,
      // cap quality to the screen, low-end TVs draw 1920 wide pages on 720p
      maxDevicePixelRatio: getScreenScale(),
      skipBufferRangeStart: 2,
      // Workaround for this issue https://github.com/video-dev/hls.js/issues/6562
      // https://mediahub-platform.atlassian.net/browse/RTV-996
      ...(window.navigator.userAgent?.includes?.('Tizen 7.0')
        ? {
            // backBuffer length is called liveBackBufferLength in this version
            // And it's set to infinity by default
            // backBufferLength: -1,
            enableWorker: false
            // already 30mb, but keep this value if primary has changed
            // maxBufferSize: 30 * 1000 * 1000,
          }
        : {})
    };
  };

  const reloadSource = (src: string, channelId?: string) => {
    if (!hls.current) {
      return;
    }
    // props still hold the previous channel during a preload
    sourceChannelIdRef.current = channelId;
    const options = getHlsOptions();
    hls.current.config.startPosition = options.startPosition;
    hls.current.config.defaultDvbAudioLanguage = options.defaultDvbAudioLanguage;
    // the smallest first segment downloads, converts and appends fastest
    hls.current.config.startLevel = options.startLevel;
    hls.current.config.testBandwidth = false;
    firebase.getPlayerStartTrace()?.start();
    sourceRef.current = src;
    // same instance and video element, hls.js only swaps the MediaSource underneath
    hls.current.config.reuseMediaSourceOnLoad = !freshMediaSourceRef.current;
    freshMediaSourceRef.current = false;
    hls.current.loadSource(src);
    hls.current.config.reuseMediaSourceOnLoad = options.reuseMediaSourceOnLoad;
  };

  // loads a zap before the store update, onSeeking fires once the media seeks into it
  const preload = useCallback(
    (src: string, channelId: string, onSeeking: () => void, onCancel: () => void) => {
      const instance = hls.current;
      const media = videoRef.current;
      // a cleared source loads through initVideo, which attaches media
      if (!instance || !media || !instance.media) {
        return false;
      }
      finishPreloadRef.current?.();
      const cleanup = () => {
        finishPreloadRef.current = cancelPreloadRef.current = undefined;
        clearTimeout(timer);
        media.removeEventListener('seeking', finish);
        instance.off(Hls.Events.ERROR, onError);
      };
      const finish = () => {
        if (finishPreloadRef.current === finish) {
          cleanup();
          onSeeking();
        }
      };
      // the source was destroyed or replaced before the zap reached the store
      const cancel = () => {
        if (cancelPreloadRef.current === cancel) {
          cleanup();
          onCancel();
        }
      };
      const onError = (_event: unknown, data: ErrorData) => data.fatal && finish();
      const timer = setTimeout(finish, PRELOAD_COMMIT_TIMEOUT);
      finishPreloadRef.current = finish;
      cancelPreloadRef.current = cancel;
      media.addEventListener('seeking', finish);
      instance.on(Hls.Events.ERROR, onError);
      sourceReadyNotified.current = sourceBuffered.current = false;
      sourceReadyEventRef.current = undefined;
      playWatchdogTimer();
      startPositionRef.current = -1;
      preloadedSrcRef.current = src;
      reloadSource(src, channelId);
      return true;
    },
    []
  );

  const initVideo = useCallback(() => {
    if (!videoRef.current) {
      return;
    }
    cancelPreloadRef.current?.();
    sourceReadyNotified.current = sourceBuffered.current = false;
    sourceReadyEventRef.current = undefined;
    if (Hls.isSupported()) {
      playWatchdogTimer();
      startPositionRef.current = isNil(propsRef.current.startPosition)
        ? -1
        : (propsRef.current.startPosition as number);

      if (hls.current) {
        const detached = !hls.current.media;
        reloadSource(props.src as string, propsRef.current.channelId);
        // attach after loading, so hls.js does not try to reuse a MediaSource it no longer has
        detached && hls.current?.attachMedia(videoRef.current);
        return;
      }

      // create a new HLS object
      sourceChannelIdRef.current = propsRef.current.channelId;
      const hlsOptions = getHlsOptions();
      const instance = (hls.current = new Hls(hlsOptions));

      instance.on(Hls.Events.DESTROYING, () => {
        if (hls.current === instance) {
          hls.current = null;
        }
      });

      // every load needs its own ready event
      instance.on(Hls.Events.MANIFEST_LOADING, (_event: unknown, data: ManifestLoadingData) => {
        sourceReadyNotified.current = sourceBuffered.current = false;
        sourceReadyEventRef.current = undefined;
        sourceRef.current = data.url;
      });

      // bind video element to this HLS object
      hls.current?.attachMedia(videoRef.current);

      firebase.getPlayerStartTrace()?.start();
      sourceRef.current = props.src;
      // video and hls.js are now bound together!
      hls.current?.loadSource(props.src as string);
      hls.current?.on(
        Hls.Events.DVB_SUBTITLE_TRACKS_UPDATED,
        (event: any, data: SubtitleTracksUpdatedData) => {
          propsRef.current.onSubtitlesUpdated && propsRef.current.onSubtitlesUpdated(data);
          if (videoRef.current) {
            // Enable all tracks
            for (let i = 0; i < videoRef.current.textTracks.length; i++) {
              const track = videoRef.current.textTracks[i];
              if (track.mode === 'disabled') track.mode = 'hidden';
            }
          }
          switchToDefaultResource(data.subtitleTracks, 'subtitles', 'subtitleTrack');
        }
      );
      hls.current?.on(
        Hls.Events.DVB_AUDIO_TRACKS_UPDATED,
        (_event: unknown, data: { audioTracks: DVBAudioTrack[] }) => {
          dvbAudioTracks.current = data.audioTracks;
          propsRef.current.onDvbAudioTracksUpdated &&
            propsRef.current.onDvbAudioTracksUpdated(data);
          switchToDefaultResource(data.audioTracks || [], 'audio', 'dvbAudioTrack');
        }
      );

      hls.current?.on(Hls.Events.DVB_AUDIO_TRACK_SWITCHED, (_event: any, data: { id: number }) => {
        propsRef.current.onDvbAudioTrackSwitch && propsRef.current.onDvbAudioTrackSwitch(data);
      });

      hls.current?.on(Hls.Events.BUFFER_CODECS, (_event: any, data: BufferCodecsData) => {
        data.video?.codec && console.log('Video codec:', data.video.codec);
        const codec = data.video?.codec || 'unknown';
        const mimeVideoCodec = `video/mp4; codecs="${codec}"`;
        const supported = MediaSource.isTypeSupported(mimeVideoCodec);
        if (!supported) {
          console.warn('Codec not supported:', mimeVideoCodec);
          // the overlay belongs to the new channel, so the store has to catch up first
          finishPreloadRef.current?.();
        }
        propsRef.current.onCodecCheck && propsRef.current.onCodecCheck(supported, codec);
      });

      hls.current?.on(
        Hls.Events.SUBTITLE_TRACK_SWITCH,
        (_event: any, data: SubtitleTrackSwitchData) => {
          propsRef.current.onSubtitleTrackSwitch && propsRef.current.onSubtitleTrackSwitch(data);
        }
      );

      hls.current?.on(Hls.Events.LEVEL_SWITCHED, (_event: any, data: LevelSwitchedData) => {
        propsRef.current.onLevelSwitched &&
          propsRef.current.onLevelSwitched(data, hls.current?.autoLevelEnabled);
      });

      hls.current?.on(Hls.Events.FRAG_BUFFERED, () => {
        sourceBuffered.current = true;
      });

      hls.current?.on(Hls.Events.BUFFER_APPENDED, (_event: any, data: BufferAppendedData) => {
        propsRef.current.onBufferAppended && propsRef.current.onBufferAppended(data);
      });

      hls.current?.on(Hls.Events.BUFFER_APPENDING, (_event: any, data: BufferAppendingData) => {
        propsRef.current.onBufferAppending && propsRef.current.onBufferAppending(data);
      });

      // Tizen 3 can stall at the end of reused media without firing ended
      hls.current?.on(Hls.Events.MEDIA_ENDED, (_event: any, data: MediaEndedData) => {
        if (data.stalled && videoRef.current) {
          videoRef.current.dispatchEvent(new Event('ended'));
        }
      });

      hls.current?.on(Hls.Events.MANIFEST_PARSED, (event: any, data: ManifestParsedData) => {
        handleInitialVideoLevel(data.levels);
        const prefetchedBitrate = getPrefetchedBitrate(sourceRef.current);
        const prefetchedLevel = data.levels.findIndex((l) => l.bitrate === prefetchedBitrate);
        if (hls.current && prefetchedLevel !== -1 && hls.current.autoLevelEnabled) {
          hls.current.startLevel = prefetchedLevel;
        }
        clearPrefetch(sourceRef.current);
        hls.current?.startLoad(startPositionRef.current);
        propsRef.current.onLevelsUpdated &&
          propsRef.current.onLevelsUpdated(data.levels, hls.current?.autoLevelEnabled);
      });
      hls.current?.on(Hls.Events.ERROR, async (_event: any, data: ErrorData) => {
        switch (data.details) {
          case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
            break;
          case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
            console.warn('Timeout while loading manifest');
            break;
          case Hls.ErrorDetails.MANIFEST_PARSING_ERROR:
            console.warn('Error while parsing manifest:' + data.reason);
            break;
          case Hls.ErrorDetails.LEVEL_LOAD_ERROR:
            console.warn('Error while loading level playlist');
            break;
          case Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT:
            console.warn('Timeout while loading level playlist');
            break;
          case Hls.ErrorDetails.LEVEL_SWITCH_ERROR:
            console.warn('Error while trying to switch to level ' + data.level);
            break;
          case Hls.ErrorDetails.FRAG_LOAD_ERROR:
            console.warn('Error while loading fragment ' + data.frag?.url);
            break;
          case Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
            console.warn('Timeout while loading fragment ' + data.frag?.url);
            break;
          // case Hls.ErrorDetails.FRAG_LOOP_LOADING_ERROR:
          //   console.warn('Fragment-loop loading error');
          //   break;
          case Hls.ErrorDetails.FRAG_DECRYPT_ERROR:
            console.warn('Decrypting error:' + data.reason);
            break;
          case Hls.ErrorDetails.FRAG_PARSING_ERROR:
            console.warn('Parsing error:' + data.reason);
            break;
          case Hls.ErrorDetails.KEY_LOAD_ERROR:
            console.warn('Error while loading key ' + data.frag?.decryptdata?.uri);
            break;
          case Hls.ErrorDetails.KEY_LOAD_TIMEOUT:
            console.warn('Timeout while loading key ' + data.frag?.decryptdata?.uri);
            break;
          case Hls.ErrorDetails.BUFFER_APPEND_ERROR:
            console.warn(
              'Buffer append error',
              data.error,
              'fatal:',
              data.fatal,
              'sb:',
              data.sourceBufferName
            );
            break;
          case Hls.ErrorDetails.BUFFER_ADD_CODEC_ERROR:
            console.warn('Buffer add codec error for ' + data.mimeType + ':' + data.err?.message);
            break;
          case Hls.ErrorDetails.BUFFER_APPENDING_ERROR:
            console.warn(
              'Buffer appending error',
              data.error,
              'fatal:',
              data.fatal,
              'sb:',
              data.sourceBufferName
            );
            break;
          case Hls.ErrorDetails.BUFFER_STALLED_ERROR:
            console.warn('Buffer stalled error');
            break;
          default:
            break;
        }

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              if (
                [
                  Hls.ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR,
                  Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR
                ].includes(data.details)
              ) {
                // Don't try to recover if the error is related to unsupported codecs, as it's not going to work and will just cause more errors
                console.warn('Cannot recover from error related to unsupported codecs');
                propsRef.current.onCodecCheck && propsRef.current.onCodecCheck(false);
                return;
              }
              console.warn('Fatal media error encountered, trying to recover');
              break;
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.warn('Fatal network error', data);
              await handleHlsNetworkError({ ...data });
              break;
            default:
              // cannot recover
              console.warn('An unrecoverable error occurred');
              break;
          }
        }
        propsRef.current.onHlsError && propsRef.current.onHlsError(data);
      });
    }

    // hls.js is not supported on platforms that do not have Media Source Extensions (MSE) enabled.
    // When the browser has built-in HLS support (check using `canPlayType`), we can provide an HLS manifest (i.e. .m3u8 URL) directly to the video element throught the `src` property.
    // This is using the built-in support of the plain video element, without using hls.js.
    else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      console.warn('Playing video on native:', props);
      videoRef.current.src = props.src as string;
      videoRef.current.addEventListener('canplay', playVideoOnNative);
    } else {
      console.warn('Could not play video', props);
    }
  }, [props.src]);

  const handleHlsNetworkError = useCallback(
    async (data: ErrorData) => {
      const error = parseError(data);

      // Handling if it's a custom error:
      // SignOuts the user
      // and based on the `subcode` property of response
      // redirects to Login or to TooManyUsers page
      if (error?.status === STATUS_CODE_BAD_REQUEST && error?.subcode) {
        //
        if (!PLAYER_ERROR_REDIRECT_MAP[error.subcode]) {
          // Try to refresh token first
          const success = await refreshToken();
          // Exit function if refresh token was successful
          if (success) return;
        }
        //
        if (ERROR_CODES_THAT_REQUIRE_LOGIN.includes(error.subcode)) {
          signOut();
          propsRef.current.removeUserDetails();
        }
        const redirect = ERROR_CODES_THAT_REQUIRE_LOGIN.includes(error.subcode)
          ? REDIRECT_DEFAULT_ROUTE
          : PLAYER_ERROR_REDIRECT_MAP[error.subcode];
        propsRef.current.setError(error.subcode, redirect);
        propsRef.current.resetVideo();
      }
    },
    [refreshToken, signOut]
  );

  const playVideoOnNative = useCallback(() => {
    try {
      if (!videoRef.current) {
        return;
      }
      videoRef.current.removeEventListener('canplay', playVideoOnNative);

      if (isNumber(props.startPosition) && props.startPosition > 0) {
        videoRef.current.currentTime = props.startPosition;
      }

      videoRef.current.play();
    } catch (error) {
      console.warn('Error while trying to play video on native', error);
    }
  }, [videoRef.current]);

  // a preloaded zap loads a channel props do not hold yet
  const getSourceChannelId = () => sourceChannelIdRef.current || propsRef.current.channelId;

  const getResourceConfiguration = useCallback((resourceKey: SettingsKey) => {
    try {
      const channelId = getSourceChannelId();
      if (!channelId) {
        throw new Error('No channel id');
      }
      const { liveChannels, profileSettings } = propsRef.current;
      const { channels } = profileSettings.react_tv_settings;
      const defaultResource = liveChannels?.[channelId]?.[resourceKey] || 'und';
      const individualResource = findDeviceSettingsItem(channels, channelId)?.[resourceKey];
      const globalResource = profileSettings.react_tv_settings[resourceKey];
      return { globalResource, individualResource, defaultResource };
    } catch (error) {
      console.warn(error);
      return { globalResource: 'und', individualResource: 'und', defaultResource: 'und' };
    }
  }, []);

  const getDefaultResource = useCallback((resourceKey: SettingsKey) => {
    const { liveChannels } = propsRef.current;
    const channelId = getSourceChannelId();
    if (!channelId) {
      throw new Error('No channel id');
    }
    return liveChannels?.[channelId]?.[resourceKey] || 'und';
  }, []);

  const resolvePreferredResource = useCallback(
    (resourceKey: SettingsKey) => {
      const { globalResource, individualResource, defaultResource } =
        getResourceConfiguration(resourceKey);
      // "und"/null mean "use default": fall through to the channel default track.
      // "zxx" (subtitles off) is a real value and is kept.
      const pick = (v: any) => (v == null || v === LANG_DEFAULT ? undefined : v);
      return pick(individualResource) || pick(globalResource) || defaultResource || LANG_DEFAULT;
    },
    [getResourceConfiguration]
  );

  const switchToDefaultResource = (
    resourceList: any[],
    resourceKey: SettingsKey,
    fieldToUpdate: 'subtitleTrack' | 'dvbAudioTrack'
  ) => {
    try {
      if (
        isEmpty(resourceList) ||
        (hls.current && isNil(hls.current[fieldToUpdate])) ||
        !hls.current ||
        !getSourceChannelId()
      ) {
        return;
      }
      const newResource = resolvePreferredResource(resourceKey);

      const newResourceIndex =
        newResource === -1 || newResource === SUBTITLES_OFF
          ? -1
          : findLastIndex(resourceList, ({ name: resourceName }) => {
              return resourceName === newResource;
            });
      // If the resource is not found, fallback to the first one in the list
      if (newResourceIndex === -1 && newResource !== -1 && newResource !== SUBTITLES_OFF) {
        const defaultResource = getDefaultResource(resourceKey);
        const defaultResourceIndex = findIndex(resourceList, ({ name: resourceName }) => {
          return resourceName === defaultResource;
        });
        console.warn(
          'Default resource not found, falling back to the default resource in the list',
          { defaultResource }
        );
        hls.current[fieldToUpdate] = defaultResourceIndex === -1 ? 0 : defaultResourceIndex;
      } else {
        const resourceId = newResourceIndex === -1 ? -1 : resourceList[newResourceIndex].id;
        hls.current[fieldToUpdate] = resourceId;
      }
    } catch (error) {
      console.warn(error);
      if (hls.current) {
        hls.current[fieldToUpdate] = resourceList[0].id;
      }
    }
  };

  const setManualLevel = (level: number) => {
    if (!hls.current) {
      return;
    }
    const isAuto = hls.current.autoLevelEnabled;
    if (level === -1 ? isAuto : !isAuto && hls.current.loadLevel === level) {
      return;
    }
    hls.current.currentLevel = level;
  };

  const handleInitialVideoLevel = useCallback((levels: Level[]) => {
    const { profileSettings } = propsRef.current;
    const channelId = sourceChannelIdRef.current;
    if (!levels.length || !hls.current) {
      return;
    }
    try {
      const channelItem = findDeviceSettingsItem(
        profileSettings.react_tv_settings.channels,
        channelId
      ) as ChannelSetting;
      const individualSettingResolution = channelItem && channelItem.resolution;
      const globalSettingResolution = profileSettings.react_tv_settings.resolution;
      const resolution = individualSettingResolution || globalSettingResolution || RESOLUTION_AUTO;
      if (resolution === RESOLUTION_AUTO) {
        setManualLevel(-1);
        return;
      }
      // "1920x1080" -> 1080; legacy bare "1080" -> 1080
      const resolutionNumber = Number(String(resolution).split('x').pop());
      const levelHeights = levels.map((level) => level.height);
      const targetResolution = findClosestNumber(levelHeights, resolutionNumber);
      const level = findLastIndex(levelHeights, (height) => targetResolution >= height);
      setManualLevel(level);
    } catch (error) {
      console.warn(error);
      setManualLevel(-1);
    }
  }, []);

  const getVideoRef = useCallback((node: HTMLVideoElement) => {
    if (node) {
      videoRef.current = node;
    }
  }, []);

  const clearPlayWatchdogTimer = () =>
    playWatchdogTimerId.current && clearTimeout(playWatchdogTimerId.current);

  const playWatchdogTimer = useCallback(() => {
    clearPlayWatchdogTimer();
    playWatchdogTimerId.current = setTimeout(() => {
      if (propsRef.current.autoPlay === false) return;
      if (
        videoRef.current &&
        (videoRef.current.paused || videoRef.current.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)
      ) {
        console.warn(
          `Video doesn't start for more than ${
            variables.AUTOPLAY_TIMEOUT / 1000
          } seconds, trying to play it again`
        );
        if (!videoRef.current.paused) videoRef.current.pause();
        // Tizen 3 (Chrome 47) returns undefined instead of a promise
        videoRef.current.play()?.catch((error) => {
          console.warn('Error while trying to play video', error);
        });
      }
    }, variables.AUTOPLAY_TIMEOUT);
  }, []);

  // a previous zap's seek can still end after the next source started loading
  const isNewSourceReady = () =>
    !sourceReadyNotified.current && (!hls.current || sourceBuffered.current);

  const notifySourceReady = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    sourceReadyNotified.current = true;
    firebase.getPlayerStartTrace()?.stop();
    if (propsRef.current.onCanPlay) {
      propsRef.current.onCanPlay(event);
    } else {
      sourceReadyEventRef.current = event;
    }
  };

  // the player listens again once the preloaded zap's store update renders
  useEffect(() => {
    const event = sourceReadyEventRef.current;
    if (props.onCanPlay && event) {
      sourceReadyEventRef.current = undefined;
      props.onCanPlay(event);
    }
  }, [props.onCanPlay]);

  const onCanPlay = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      firebase.getPlayerStartTrace()?.stop();
      playWatchdogTimer();
      if (isNewSourceReady()) {
        notifySourceReady(event);
      }
    },
    [props.onCanPlay]
  );

  const onPlaying = useCallback((event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    // Reused media can keep HAVE_ENOUGH_DATA across a source change, so canplay may not fire
    if (isNewSourceReady()) {
      notifySourceReady(event);
    }
    propsRef.current.onPlaying?.(event);
  }, []);

  const onTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      playWatchdogTimer();
      // without a seek, reused media plays into the new source firing neither canplay nor playing
      const media = videoRef.current;
      if (
        !sourceReadyNotified.current &&
        sourceBuffered.current &&
        media &&
        !media.paused &&
        media.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
      ) {
        notifySourceReady(e);
      }
      props.onTimeUpdate?.(e);
    },
    [props.onTimeUpdate]
  );

  const onPause = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      // keep the watchdog when reused media pauses while switching sources
      if (sourceReadyNotified.current) {
        clearPlayWatchdogTimer();
      }
      props.onPause?.(e);
    },
    [props.onPause]
  );

  const onPlay = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      playWatchdogTimer();
      props.onPlay?.(e);
    },
    [props.onPlay]
  );

  const {
    src: _src,
    hash: _hash,
    channelId: _channelId,
    startPosition: _startPosition,
    startLevel: _startLevel,
    defaultDvbAudioLanguage: _defaultDvbAudioLanguage,
    dvbAudioTrack: _dvbAudioTrack,
    subtitleTrack: _subtitleTrack,
    liveChannels: _liveChannels,
    profileSettings: _profileSettings,
    setError: _setError,
    resetVideo: _resetVideo,
    removeUserDetails: _removeUserDetails,
    onSubtitlesUpdated: _onSubtitlesUpdated,
    onSubtitleTrackSwitch: _onSubtitleTrackSwitch,
    onDvbAudioTracksUpdated: _onDvbAudioTracksUpdated,
    onDvbAudioTrackSwitch: _onDvbAudioTrackSwitch,
    onLevelSwitched: _onLevelSwitched,
    onLevelsUpdated: _onLevelsUpdated,
    onHlsError: _onHlsError,
    onBufferAppended: _onBufferAppended,
    onBufferAppending: _onBufferAppending,
    onCodecCheck: _onCodecCheck,
    destroyWithoutSource: _destroyWithoutSource,
    ...videoElementProps
  } = props;

  return (
    <video
      {...videoElementProps}
      className={`styled-video ${props.className || ''}`}
      onCanPlay={onCanPlay}
      onTimeUpdate={onTimeUpdate}
      onPause={onPause}
      onPlay={onPlay}
      onPlaying={onPlaying}
      ref={getVideoRef}
      autoPlay={isNil(props.autoPlay) ? true : props.autoPlay}
    />
  );
});

const mapStateToProps = ({ liveChannels, profileSettings }: any) => ({
  liveChannels,
  profileSettings
});

export default React.memo(
  connect(
    mapStateToProps,
    {
      removeUserDetails,
      setError,
      resetVideo
    },
    null,
    { forwardRef: true }
  )(Video)
);
